/**
 * Recurrence Types
 *
 * Types and schemas for recurrence/schedule trigger configuration.
 * Follows LogicAppsUX patterns for schedule triggers.
 */

import { z } from 'zod';
import { timeZones } from '@/data/timeZones';
import { formatDate } from '@/utils/dateTime';

// =============================================================================
// FREQUENCY VALUES
// =============================================================================

export const FrequencyValues = ['Month', 'Week', 'Day', 'Hour', 'Minute', 'Second'] as const;
export type Frequency = (typeof FrequencyValues)[number];

export const WeekDayValues = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;
export type WeekDay = (typeof WeekDayValues)[number];

// =============================================================================
// RECURRENCE SCHEDULE
// =============================================================================

/**
 * Schedule configuration for advanced recurrence
 * Only applicable for Day, Week, and Month frequencies
 */
export interface RecurrenceSchedule {
  /** Hours of the day to run (0-23) */
  hours?: number[];
  /** Minutes of the hour to run (0-59) */
  minutes?: number[];
  /** Days of the week to run (for Week frequency) */
  weekDays?: WeekDay[];
  /** Days of the month to run (1-31, -1 for last day) (for Month frequency) */
  monthDays?: number[];
}

// =============================================================================
// RECURRENCE VALUE
// =============================================================================

/**
 * Complete recurrence configuration
 */
export interface RecurrenceValue {
  /** How often to run */
  frequency: Frequency;
  /** Run every N units of frequency */
  interval: number;
  /** When to start the schedule (ISO 8601 datetime) */
  startTime?: string;
  /** Timezone for the schedule (Windows ID or UTC offset) */
  timeZone?: string;
  /** Advanced schedule options */
  schedule?: RecurrenceSchedule;
}

// =============================================================================
// ZOD SCHEMAS
// =============================================================================

export const RecurrenceScheduleSchema = z.object({
  hours: z.array(z.number().min(0).max(23)).optional(),
  minutes: z.array(z.number().min(0).max(59)).optional(),
  weekDays: z.array(z.enum(WeekDayValues)).optional(),
  monthDays: z.array(z.number().min(-1).max(31)).optional(),
});

export const RecurrenceValueSchema = z.object({
  frequency: z.enum(FrequencyValues),
  interval: z.number().min(1).max(1000),
  startTime: z.string().optional(),
  timeZone: z.string().optional(),
  schedule: RecurrenceScheduleSchema.optional(),
});

// =============================================================================
// DEFAULTS
// =============================================================================

export const DEFAULT_RECURRENCE: RecurrenceValue = {
  frequency: 'Day',
  interval: 1,
};

// =============================================================================
// COMMON TIMEZONES
// =============================================================================

/**
 * Timezone options for recurrence configuration.
 * Includes Windows timezone IDs and UTC offset-based IDs (e.g., "UTC-08").
 * .NET 8 supports both on Linux via ICU.
 * Sourced from the shared timeZones module (@/data/timeZones) to avoid duplication.
 */
export const COMMON_TIMEZONES = timeZones;

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Check if schedule options are available for the given frequency
 */
export function supportsSchedule(frequency: Frequency): boolean {
  return frequency === 'Day' || frequency === 'Week' || frequency === 'Month';
}

/**
 * Check if weekDays are applicable for the given frequency
 */
export function supportsWeekDays(frequency: Frequency): boolean {
  return frequency === 'Week';
}

/**
 * Check if monthDays are applicable for the given frequency
 */
export function supportsMonthDays(frequency: Frequency): boolean {
  return frequency === 'Month';
}

/**
 * Get frequency unit label (singular/plural)
 */
export function getFrequencyLabel(frequency: Frequency, interval: number): string {
  const unit = frequency.toLowerCase();
  return interval === 1 ? unit : `${unit}s`;
}

/**
 * Generate human-readable preview of the recurrence schedule
 */
export function generateRecurrencePreview(recurrence: RecurrenceValue): string {
  const { frequency, interval, schedule, startTime, timeZone } = recurrence;

  const parts: string[] = [];

  // Base frequency
  if (interval === 1) {
    parts.push(`Runs every ${frequency.toLowerCase()}`);
  } else {
    parts.push(`Runs every ${interval} ${frequency.toLowerCase()}s`);
  }

  // Schedule details
  if (schedule && supportsSchedule(frequency)) {
    const scheduleParts: string[] = [];

    // Week days
    if (schedule.weekDays && schedule.weekDays.length > 0 && supportsWeekDays(frequency)) {
      if (schedule.weekDays.length === 7) {
        scheduleParts.push('every day');
      } else if (schedule.weekDays.length === 5 && !schedule.weekDays.includes('Saturday') && !schedule.weekDays.includes('Sunday')) {
        scheduleParts.push('on weekdays');
      } else {
        scheduleParts.push(`on ${schedule.weekDays.join(', ')}`);
      }
    }

    // Month days
    if (schedule.monthDays && schedule.monthDays.length > 0 && supportsMonthDays(frequency)) {
      const dayLabels = schedule.monthDays.map((d) => (d === -1 ? 'last day' : `day ${d}`));
      scheduleParts.push(`on ${dayLabels.join(', ')}`);
    }

    // Hours and minutes
    if (schedule.hours && schedule.hours.length > 0) {
      const times = schedule.hours.map((h) => {
        const mins = schedule.minutes && schedule.minutes.length > 0 ? schedule.minutes : [0];
        return mins.map((m) => `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
      }).flat();
      scheduleParts.push(`at ${times.join(', ')}`);
    } else if (schedule.minutes && schedule.minutes.length > 0) {
      scheduleParts.push(`at minute ${schedule.minutes.join(', ')} of each hour`);
    }

    if (scheduleParts.length > 0) {
      parts.push(scheduleParts.join(' '));
    }
  }

  // Timezone
  if (timeZone) {
    parts.push(`(${timeZone})`);
  }

  // Start time
  if (startTime) {
    const date = new Date(startTime);
    if (!isNaN(date.getTime())) {
      parts.push(`starting ${formatDate(date)}`);
    }
  }

  return parts.join(' ');
}
